// AI 답변 속 차트 렌더러 — 의존성 없이 SVG로 그린다.
//
// 규칙(데이터 시각화 가이드)을 코드로 고정한 지점들:
//  - 단일 시리즈는 모든 막대가 같은 색이다. 값이 클수록 진하게 칠하는 건 막대 길이를
//    색으로 한 번 더 말하는 것이라 채널만 낭비한다.
//  - 시리즈 색은 고정 순서로만 배정한다(순환·생성 금지).
//  - 막대 사이는 선이 아니라 2px 여백으로 가른다.
//  - 값 라벨과 축 글자는 절대 시리즈 색을 입지 않는다 — 옅은 색은 글자로 읽히지 않는다.
//  - 표 보기를 항상 제공한다. 색 대비가 낮은 슬롯(aqua·yellow·magenta)이 있어
//    색만으로 값을 전달하면 안 되기 때문이다.
import React, { useMemo, useState } from 'react';
import {
  SERIES_COLORS,
  computeScale,
  formatValue,
  parseChartSpec,
  type NormalizedChart,
} from './chartSpec';
import styles from './ChartBlock.module.css';

/* 여백 — 축 글자가 들어갈 자리를 포함한다. 플롯만 재면 x축 라벨이 잘린다.
   top은 막대 끝 값 라벨과 꺾은선 마커(반지름 4 + 링 2)가 들어갈 만큼 둔다.
   left는 눈금 숫자 길이에 따라 달라지므로 여기 값은 최소치이고, 실제 폭은 아래에서 잰다. */
const PAD = { top: 24, right: 16, bottom: 34, left: 52 };
/* 축 글자 한 자의 대략적 폭(11px, tabular-nums) — 3,300,000처럼 긴 눈금이 잘리지 않게
   좌측 여백을 라벨 길이에 맞춰 늘리는 데 쓴다. */
const AXIS_CHAR_W = 6.4;
const PLOT_W = 640;
const PLOT_H = 260;
const BAR_MAX_W = 24; // 막대는 슬롯을 꽉 채우지 않는다 — 남는 폭은 공기로 둔다
const GAP = 2;        // 인접 막대를 가르는 표면 여백

interface Hover {
  x: number;
  y: number;
  rows: { name: string; value: number; color: string }[];
  label: string;
}

export default function ChartBlock({ source }: { source: string }) {
  const parsed = useMemo(() => parseChartSpec(source), [source]);
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState<Hover | null>(null);

  if (!parsed.ok) {
    return (
      <div className={styles.error}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16.5v.01" />
        </svg>
        <span>{parsed.reason}</span>
      </div>
    );
  }

  const chart = parsed.spec;
  const multi = chart.series.length > 1;

  return (
    <figure className={styles.figure}>
      <figcaption className={styles.header}>
        {chart.title && <span className={styles.title}>{chart.title}</span>}
        <button
          type="button"
          className={styles.tableToggle}
          onClick={() => setShowTable(v => !v)}
          aria-expanded={showTable}
        >
          {showTable ? '차트 보기' : '표 보기'}
        </button>
      </figcaption>

      {/* 범례는 시리즈가 둘 이상일 때만 — 하나뿐이면 제목이 이미 무엇인지 말한다 */}
      {multi && (
        <div className={styles.legend}>
          {chart.series.map((s, i) => (
            <span key={s.name} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ background: SERIES_COLORS[i] }} />
              {s.name}
            </span>
          ))}
        </div>
      )}

      {showTable ? (
        <ChartTable chart={chart} />
      ) : (
        <div className={styles.plotWrap}>
          {chart.type === 'pie'
            ? <PieChart chart={chart} onHover={setHover} />
            : <AxisChart chart={chart} onHover={setHover} />}
          {hover && (
            <div className={styles.tooltip} style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
              <div className={styles.tooltipLabel}>{hover.label}</div>
              {hover.rows.map(r => (
                <div key={r.name} className={styles.tooltipRow}>
                  <span className={styles.tooltipSwatch} style={{ background: r.color }} />
                  {r.name && <span className={styles.tooltipName}>{r.name}</span>}
                  <span className={styles.tooltipValue}>{formatValue(r.value, chart.unit)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

/**
 * 차트 자리 표시 — 스트리밍 중 JSON이 다 도착하기 전에 쓴다.
 * 원문을 코드블록으로 흘리면 사용자에게 중괄호 덩어리가 그대로 보인다.
 */
export function ChartSkeleton() {
  return (
    <div className={styles.skeleton} aria-live="polite" aria-label="차트를 준비하는 중">
      <div className={styles.skeletonHeader}>
        <span className={styles.skeletonBar} style={{ width: 120 }} />
      </div>
      <div className={styles.skeletonPlot}>
        {[46, 72, 38, 60, 84].map((h, i) => (
          <span key={i} className={styles.skeletonColumn} style={{ height: `${h}%` }} />
        ))}
      </div>
      <span className={styles.skeletonNote}>차트를 그리는 중…</span>
    </div>
  );
}

/* ── 막대·꺾은선 (공통 축 프레임) ── */
function AxisChart({ chart, onHover }: { chart: NormalizedChart; onHover: (h: Hover | null) => void }) {
  const values = chart.series.flatMap(s => s.data.map(p => p.value));
  const { ticks, bottom, top } = computeScale(values, chart.type);
  const span = top - bottom || 1;

  // 눈금 숫자가 길면 좌측 여백을 넓힌다 — 고정 폭이면 백만 단위에서 앞자리가 잘린다
  const tickLabels = ticks.map(t => t.toLocaleString());
  const longestTick = Math.max(...tickLabels.map(s => s.length));
  // +20 = 라벨과 그리드 사이 8px + 글자폭 추정 오차 여유 12px
  const padLeft = Math.max(PAD.left, Math.round(longestTick * AXIS_CHAR_W) + 20);

  const W = PLOT_W + padLeft + PAD.right;
  const H = PLOT_H + PAD.top + PAD.bottom;
  const y = (v: number) => PAD.top + PLOT_H - ((v - bottom) / span) * PLOT_H;
  const bandW = PLOT_W / chart.labels.length;

  // 값 라벨은 선별적으로만 — 모든 점에 숫자를 붙이면 아무도 읽지 않는다.
  // 단일 시리즈이고 항목이 적을 때만 막대 끝에 값을 얹는다.
  const showValueLabels = chart.series.length === 1 && chart.labels.length <= 8;

  const hoverAt = (i: number, cx: number, cy: number) => onHover({
    x: (cx / W) * 100,
    y: (cy / H) * 100,
    label: chart.labels[i],
    rows: chart.series.map((s, si) => ({ name: chart.series.length > 1 ? s.name : '', value: s.data[i].value, color: SERIES_COLORS[si] })),
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.svg} role="img"
         aria-label={chart.title ?? '차트'} onMouseLeave={() => onHover(null)}>
      {/* 그리드 — 실선 hairline, 표면에서 한 단계만 벗어난다 */}
      {ticks.map((t, ti) => (
        <g key={t}>
          <line x1={padLeft} x2={padLeft + PLOT_W} y1={y(t)} y2={y(t)}
                stroke="#E2E8F0" strokeWidth="1" shapeRendering="crispEdges" />
          <text x={padLeft - 8} y={y(t) + 4} textAnchor="end" className={styles.axisText}>
            {tickLabels[ti]}
          </text>
        </g>
      ))}
      {/* 기준선 */}
      <line x1={padLeft} x2={padLeft + PLOT_W} y1={y(bottom)} y2={y(bottom)}
            stroke="#CBD5E1" strokeWidth="1" shapeRendering="crispEdges" />

      {chart.type === 'bar'
        ? <Bars chart={chart} y={y} bandW={bandW} padLeft={padLeft} baseline={y(Math.max(bottom, 0))}
                showValueLabels={showValueLabels} onHoverAt={hoverAt} />
        : <Lines chart={chart} y={y} bandW={bandW} padLeft={padLeft} onHoverAt={hoverAt} />}

      {/* x축 항목명 */}
      {chart.labels.map((label, i) => (
        <text key={label + i} x={padLeft + bandW * (i + 0.5)} y={PAD.top + PLOT_H + 20}
              textAnchor="middle" className={styles.axisText}>
          {label}
        </text>
      ))}
    </svg>
  );
}

function Bars({ chart, y, bandW, padLeft, baseline, showValueLabels, onHoverAt }: {
  chart: NormalizedChart;
  y: (v: number) => number;
  bandW: number;
  padLeft: number;
  baseline: number;
  showValueLabels: boolean;
  onHoverAt: (i: number, cx: number, cy: number) => void;
}) {
  const n = chart.series.length;
  // 시리즈가 여럿이면 한 밴드를 나눠 쓴다. 나눈 폭에서 2px를 덜어 막대끼리 붙지 않게 한다.
  const slotW = Math.min(BAR_MAX_W, (bandW * 0.72) / n);
  const groupW = slotW * n + GAP * (n - 1);

  return (
    <>
      {chart.series.map((s, si) => (
        <g key={s.name || si}>
          {s.data.map((p, i) => {
            const cx = padLeft + bandW * (i + 0.5) - groupW / 2 + si * (slotW + GAP);
            const top = Math.min(y(p.value), baseline);
            const h = Math.abs(baseline - y(p.value));
            return (
              <g key={p.label + i}>
                {/* 데이터 끝만 둥글게, 기준선 쪽은 각지게 — 길이를 왜곡하지 않는다 */}
                <path
                  d={roundedBar(cx, top, slotW, h, p.value >= 0)}
                  fill={SERIES_COLORS[si]}
                />
                {/* 히트 영역은 막대보다 넓게 — 얇은 막대를 정확히 겨냥하게 하지 않는다 */}
                <rect x={padLeft + bandW * i} y={PAD.top} width={bandW} height={PLOT_H}
                      fill="transparent"
                      onMouseEnter={() => onHoverAt(i, padLeft + bandW * (i + 0.5), top)} />
                {showValueLabels && (
                  <text x={cx + slotW / 2} y={top - 6} textAnchor="middle" className={styles.valueLabel}>
                    {p.value.toLocaleString()}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      ))}
    </>
  );
}

/** 데이터 끝 4px 라운드, 기준선 쪽은 직각 */
function roundedBar(x: number, yTop: number, w: number, h: number, positive: boolean): string {
  const r = Math.min(4, w / 2, h);
  if (h <= 0) return '';
  return positive
    ? `M${x},${yTop + h} L${x},${yTop + r} Q${x},${yTop} ${x + r},${yTop} L${x + w - r},${yTop} Q${x + w},${yTop} ${x + w},${yTop + r} L${x + w},${yTop + h} Z`
    : `M${x},${yTop} L${x},${yTop + h - r} Q${x},${yTop + h} ${x + r},${yTop + h} L${x + w - r},${yTop + h} Q${x + w},${yTop + h} ${x + w},${yTop + h - r} L${x + w},${yTop} Z`;
}

function Lines({ chart, y, bandW, padLeft, onHoverAt }: {
  chart: NormalizedChart;
  y: (v: number) => number;
  bandW: number;
  padLeft: number;
  onHoverAt: (i: number, cx: number, cy: number) => void;
}) {
  const x = (i: number) => padLeft + bandW * (i + 0.5);
  return (
    <>
      {chart.series.map((s, si) => (
        <g key={s.name || si}>
          <path
            d={s.data.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.value)}`).join(' ')}
            fill="none" stroke={SERIES_COLORS[si]} strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round"
          />
          {s.data.map((p, i) => (
            // 표면색 링 — 선끼리 겹치는 지점에서도 점이 살아 있게 한다
            <circle key={p.label + i} cx={x(i)} cy={y(p.value)} r="4"
                    fill={SERIES_COLORS[si]} stroke="#ffffff" strokeWidth="2" />
          ))}
        </g>
      ))}
      {/* 세로 밴드 전체를 히트 영역으로 — 점을 정확히 짚지 않아도 된다 */}
      {chart.labels.map((label, i) => (
        <rect key={label + i} x={padLeft + bandW * i} y={PAD.top} width={bandW} height={PLOT_H}
              fill="transparent" onMouseEnter={() => onHoverAt(i, x(i), y(chart.series[0].data[i].value))} />
      ))}
    </>
  );
}

/* ── 파이 (부분-전체, 3~6조각) ── */
function PieChart({ chart, onHover }: { chart: NormalizedChart; onHover: (h: Hover | null) => void }) {
  const data = chart.series[0].data;
  const total = data.reduce((sum, p) => sum + p.value, 0) || 1;
  const R = 92;
  const CX = 116;
  const CY = 130;
  const W = 500;
  const H = 260;
  /* 라벨 영역 — 원 오른쪽. 이름은 왼쪽 정렬, 비율은 오른쪽 끝에 맞춰 자릿수가 흔들려도
     열이 어긋나지 않게 한다. LABEL_END는 W 안쪽에 둔다(넘치면 글자가 잘린다). */
  const LABEL_X = 240;
  const LABEL_END = W - 16;

  let angle = -Math.PI / 2; // 12시 방향에서 시작
  const slices = data.map((p, i) => {
    const sweep = (p.value / total) * Math.PI * 2;
    const start = angle;
    angle += sweep;
    return { point: p, start, end: angle, color: SERIES_COLORS[i], pct: (p.value / total) * 100 };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.svg} role="img"
         aria-label={chart.title ?? '파이 차트'} onMouseLeave={() => onHover(null)}>
      {slices.map((s, i) => {
        // 툴팁은 조각 중심 위로 — 고정 위치면 위쪽 조각에서 카드 밖으로 잘려 나간다
        const mid = (s.start + s.end) / 2;
        const tx = CX + Math.cos(mid) * R * 0.62;
        const ty = CY + Math.sin(mid) * R * 0.62;
        return (
          <path
            key={s.point.label + i}
            d={arcPath(CX, CY, R, s.start, s.end)}
            fill={s.color}
            stroke="#ffffff"
            strokeWidth={GAP} /* 조각 사이는 선이 아니라 표면색 여백으로 가른다 */
            onMouseEnter={() => onHover({
              x: (tx / W) * 100,
              y: (ty / H) * 100,
              label: s.point.label,
              rows: [{ name: '', value: s.point.value, color: s.color }],
            })}
          />
        );
      })}
      {/* 조각 이름과 비율은 오른쪽에 직접 표기 — 색 대비가 낮은 슬롯이 있어
          색만으로 식별하게 두지 않는다 */}
      {slices.map((s, i) => {
        // 조각 수에 따라 세로로 중앙 정렬 — 3조각일 때 위쪽에 몰리지 않게 한다
        const rowY = CY - ((slices.length - 1) * 28) / 2 + i * 28;
        return (
          <g key={'label' + i} transform={`translate(${LABEL_X}, ${rowY})`}>
            <rect x="0" y="-5" width="10" height="10" rx="2" fill={s.color} />
            <text x="18" y="0" className={styles.pieLabel}>{s.point.label}</text>
            <text x={LABEL_END - LABEL_X} y="0" textAnchor="end" className={styles.piePct}>
              {s.pct.toFixed(1)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
  const x1 = cx + r * Math.cos(start);
  const y1 = cy + r * Math.sin(start);
  const x2 = cx + r * Math.cos(end);
  const y2 = cy + r * Math.sin(end);
  const large = end - start > Math.PI ? 1 : 0;
  return `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`;
}

/* ── 표 보기 — 모든 값에 도달할 수 있는 경로. 툴팁이 유일한 통로가 되면 안 된다 ── */
function ChartTable({ chart }: { chart: NormalizedChart }) {
  const multi = chart.series.length > 1;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>항목</th>
            {multi
              ? chart.series.map(s => <th key={s.name}>{s.name}</th>)
              : <th>값{chart.unit ? ` (${chart.unit})` : ''}</th>}
          </tr>
        </thead>
        <tbody>
          {chart.labels.map((label, i) => (
            <tr key={label + i}>
              <td>{label}</td>
              {chart.series.map((s, si) => (
                <td key={si} className={styles.num}>{formatValue(s.data[i].value, multi ? chart.unit : undefined)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
